import React, { useEffect, useState, useCallback, useMemo, memo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Message } from '@dev-streetbeat/chat/types';
import { TextResponse } from '../responses/TextResponse';
import { MessageArtifact } from 'pages/assistant/components/artifacts/MessageArtifact';
import { RebalancingTabbedView } from 'pages/assistant/components/artifacts/components/fineco-portfolio/RebalancingTabbedView';
import { FinecoAgentIcon } from 'shared/assets/svg/FinecoAgentIcon';
import styles from './chatMessage.module.scss';
import PortfolioSkeleton from '../PortfolioSkeleton';
import useThreadsStore from 'shared/store/threadsStore';
import Breakpoint from 'shared/components/responsive/Breakpoint';
import { useTabletAndBelowMediaQuery } from 'shared/store/responsiveStore';
import classNames from 'classnames';
import ResponseFeedback from './ResponseFeedback';
import { useParams } from 'react-router-dom';
import TaskStatuses from './TaskStatuses';
import { dedupeFinecoPortfolioArtifacts } from 'utils/portfolioUtils';

type Props = {
  message?: Message;
  thinking?: boolean;
  isConnectionOpen?: boolean;
  onSend?: (text: string) => void;
  disabled?: boolean;
  skeletonLoader?: boolean;
};

export const ChatAgentMessage: React.FC<Props> = memo(
  ({ message, thinking, isConnectionOpen, onSend, disabled, skeletonLoader }) => {
    const { t } = useTranslation();
    const { threadId } = useParams();
    const [finecoPortfolioAndPortfolioStoryTogether, setFinecoPortfolioAndPortfolioStoryTogether] = useState(false);
    const [isRebalancingWithComparison, setIsRebalancingWithComparison] = useState(false);

    const isTabletOrBelow = useTabletAndBelowMediaQuery();

    const hideTempStatusUpdate = useThreadsStore((state) => state.hideTempStatusUpdate);

    const artifacts = useMemo(() => dedupeFinecoPortfolioArtifacts(message?.artifacts ?? []), [message?.artifacts]);

    const getResponseComponent = useCallback(() => {
      // The Wrapper list artifact is a complete, self-describing answer and the
      // Bank asked for no text above it (issue #89). Whatever text the pipeline
      // emits for that turn (model filler, translator fallout) is hidden here.
      const hasWrapperList = message?.artifacts?.some(
        (artifact: any) =>
          artifact.type === 'custom_data' && artifact.custom_data?.type === 'fineco_wrapper_list',
      );
      if (hasWrapperList) return null;
      if (message?.content?.some((b: { type: string }) => b.type === 'text')) {
        return <TextResponse message={message} />;
      }
    }, [message]);

    useEffect(() => {
      const hasPortfolio = artifacts.some(
        (artifact: any) => artifact.type === 'custom_data' && artifact.custom_data?.type === 'fineco_portfolio',
      );

      const hasPortfolioStory = artifacts.some(
        (artifact: any) => artifact.type === 'custom_data' && artifact.custom_data?.type === 'portfolio_story',
      );

      const hasComparison = artifacts.some(
        (artifact: any) => artifact.type === 'custom_data' && artifact.custom_data?.type === 'fineco_portfolio_comparison',
      );

      setFinecoPortfolioAndPortfolioStoryTogether(hasPortfolio && hasPortfolioStory);
      setIsRebalancingWithComparison(hasPortfolio && hasComparison);
    }, [artifacts]);

    // Determine if we should show feedback (only for completed non-temporary messages)
    const shouldShowFeedback =
      message?.id && message.id !== 'temp' && message.id !== 'temp-status-update' && !thinking && !skeletonLoader;

    if (!message) return null;

    if (thinking && !hideTempStatusUpdate) {
      return (
        <div className={styles.chatAgentContainer}>
          <div className={styles.chatAgentStatusUpdateContainer}>
            <FinecoAgentIcon className="icon-size-32-32" />
            <div className={styles.statusContainer}>
              <div className={styles.agentStatusMessages}>
                <TaskStatuses />
              </div>
              <PortfolioSkeleton />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.chatAgentContainer}>
        {skeletonLoader && !hideTempStatusUpdate && (
          <div className={styles.chatAgentStatusUpdateContainer}>
            <FinecoAgentIcon className="icon-size-32-32" />
            <div className={styles.statusContainer}>
              <div className={styles.agentStatusMessages}>
                <TaskStatuses />
              </div>
              <PortfolioSkeleton />
            </div>
          </div>
        )}
        {!skeletonLoader && (
          <div className="mt-2">
            <div
              className={classNames('flex min-w-0 flex-nowrap items-start', {
                'gap-4': !isTabletOrBelow,
                'flex-col': isTabletOrBelow,
              })}
            >
              <div
                className={classNames('flex-shrink-0', {
                  'flex gap-4': isTabletOrBelow,
                })}
                style={{
                  width: isTabletOrBelow ? 'fit-content' : 32,
                  height: 32,
                }}
              >
                <FinecoAgentIcon className="icon-size-32-32" />
                <Breakpoint md down>
                  <div className={styles.agentTitle}>{t('assistant.agent')}</div>
                </Breakpoint>
              </div>
              <div className="w-full min-w-0 flex-1">
                <Breakpoint lg up>
                  <div className={styles.agentTitle}>{t('assistant.agent')}</div>
                </Breakpoint>

                <div className="group relative break-words">{getResponseComponent()}</div>
                <div className={styles.artifactsContainer}>
                  {isRebalancingWithComparison && (
                    <div className={styles.artifactWrapper}>
                      <RebalancingTabbedView
                        message={message}
                        artifacts={artifacts}
                        finecoPortfolioAndPortfolioStoryTogether={finecoPortfolioAndPortfolioStoryTogether}
                      />
                    </div>
                  )}
                  {artifacts.map((artifact, index) => {
                    const customType = (artifact as any).custom_data?.type;
                    const isCitation = artifact.type === 'custom_data' && customType === 'citation';

                    // Skip portfolio & comparison artifacts when rendered in tabbed view
                    if (
                      isRebalancingWithComparison &&
                      artifact.type === 'custom_data' &&
                      (customType === 'fineco_portfolio' || customType === 'fineco_portfolio_comparison')
                    ) {
                      return null;
                    }

                    return (
                      <div key={index} className={isCitation ? styles.citationWrapper : styles.artifactWrapper}>
                        <MessageArtifact
                          finecoPortfolioAndPortfolioStoryTogether={finecoPortfolioAndPortfolioStoryTogether}
                          message={message}
                          artifact={artifact}
                          isConnectionOpen={isConnectionOpen}
                          onSend={onSend}
                          disabled={disabled}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className={styles.messageControls}>

              {shouldShowFeedback && threadId && <ResponseFeedback messageId={message.id} />}
            </div>
          </div>
        )}
      </div>
    );
  },
);

ChatAgentMessage.displayName = 'ChatAgentMessage';

export default ChatAgentMessage;
